/** @jsx jsx */

import { css, jsx } from '@emotion/react';

import { labelStyles } from '@zapier/style-encapsulation';
import { useEffect, useState } from 'react';
import type { PlayerEvents } from './types';

const Styles = labelStyles('YouTubeVideo', {
  root: css`
    position: relative;
    width: 100%;
    height: 0;
  `,
  video: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
});

type Props = {
  /**
   * The ID of the video to be displayed.
   * For example: in the URL `https://www.youtube.com/watch?v=QEmNOlXf-HY`, it would be `QEmNOlXf-HY`.
   */
  youTubeId: string;
  /**
   * The title of the video.
   */
  title: string;
  /**
   * Aspect ratio at which to render the video.
   */
  aspectRatio?: number;
  /**
   * Whether to automatically start playing the video.
   * When `true`, the video will autoplay in browsers that permit it.
   * Recommended: Also include the mute prop, since some browsers do not permit autoplay unless a video is muted.
   * See: https://developer.chrome.com/blog/autoplay/
   */
  autoplay?: boolean;
  /**
   * Whether to allow the video to be displayed full screen.
   * When `true`, allow the user to view the video in `fullscreen` mode.
   */
  allowFullscreen?: boolean;
  /**
   * Whether to mute the video.
   * When `true`, the video will initially play muted.
   * This setting is required for some browsers to allow autoplay.
   */
  mute?: boolean;

  /**
   * Callbacks for YouTube Iframe API events.
   * @see https://developers.google.com/youtube/iframe_api_reference#Events
   */
  events?: PlayerEvents;
};

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export const useYouTubeIframeAPI = (
  youTubeId: string,
  events?: PlayerEvents
) => {
  useEffect(() => {
    const loadScriptTag = (scriptSrc: string) => {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      document.body.append(script);
    };

    const loadYouTubeIframeApi = () => {
      // Creates a promise that is resolved when the iframe API is ready.
      // Allows creating a player for each of multiple iframes in the same page.
      const youTubeIframeAPIReady = new Promise<Window['YT']>((resolve) => {
        const scriptSrc = 'https://www.youtube.com/iframe_api';
        const existingScript = document.querySelector(
          `script[src="${scriptSrc}"]`
        );

        if (!existingScript) {
          loadScriptTag(scriptSrc);
        }

        const previousIframeApiReady = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
          if (previousIframeApiReady) {
            previousIframeApiReady();
          }
          resolve(window.YT);
        };
      });

      return youTubeIframeAPIReady;
    };

    const loadIframe = () => {
      return loadYouTubeIframeApi()
        .then(
          (youTubeIframeAPI) =>
            new youTubeIframeAPI.Player(`youtube-video-${youTubeId}`, {
              events,
            })
        )
        .catch(() => {});
    };

    if (events && Object.keys(events).length) {
      loadIframe();
    }
  }, [events, youTubeId]);
};

/**
 * Renders a YouTube video given a `youTubeId`.
 */
export const YouTubeVideo = ({
  title,
  youTubeId,
  allowFullscreen = true,
  aspectRatio = 16 / 9,
  autoplay = false,
  events = undefined,
  mute = false,
}: Props) => {
  const [src, setSrc] = useState(
    // ?rel=0 makes only videos by Zapier show as related videos
    `https://www.youtube.com/embed/${youTubeId}?rel=0&autoplay=${Number(
      autoplay
    )}&mute=${Number(mute)}`
  );

  useEffect(() => {
    if (events && Object.keys(events)?.length) {
      setSrc(
        (prevState) =>
          `${prevState}&enablejsapi=1&origin=${window.location.origin}`
      );
    }
  }, [events]);

  useYouTubeIframeAPI(youTubeId, events);

  const style = {
    paddingBottom: `${aspectRatio ** -1 * 100}%`,
  };

  return (
    <div css={Styles.root} style={style} data-zds>
      <iframe
        id={`youtube-video-${youTubeId}`}
        allow={allowFullscreen ? 'fullscreen' : undefined}
        css={Styles.video}
        frameBorder="0"
        src={src}
        title={title}
      ></iframe>
    </div>
  );
};
